<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DLoading,
  DModal,
  DSelectFieldSearch,
  DTable,
  DTextField,
  DToggleField,
  Edit03Icon,
  Trash03Icon
} from 'front-library'
import { Field } from '@/app/core/services/forms'
import { useNotificationsStore } from '@/store/notifications'
import {
  RemoteColumn,
  RemoteField,
  RemoteOptionFilter,
  RemoteRow,
  RemoteTableConfig,
  RemoteTableMessages,
  SourceItem,
  TagContext,
  createRemoteRow,
  deleteRemoteRow,
  listRemoteRows,
  loadRemoteSource,
  matchesSourceFilter,
  updateRemoteRow,
  usesCurrentId
} from '@/app/core/services/remote-table'

const props = defineProps<{
  field: Field
  label?: any
  readonly?: boolean
  allModel?: any
  modelValue?: any
  error?: any
  disabled?: any
  options?: any
  placeholder?: any
  hint?: any
  headerTitle?: any
  helpText?: any
  titleBtnSave?: any
  validation?: any
  class?: any
}>()

const route = useRoute()
const { t } = useI18n()
const notifications = useNotificationsStore()

const config = computed<RemoteTableConfig>(
  () => props.field.props as RemoteTableConfig
)
const idKey = computed(() => config.value.idKey ?? 'id')
const tagContext = computed<TagContext>(() => ({
  currentId: route.params.id as string | undefined,
  model: props.allModel ?? {}
}))
const canLoad = computed(
  () => !usesCurrentId(config.value) || Boolean(tagContext.value.currentId)
)
const actions = computed<string[]>(
  () => config.value.actions ?? ['add', 'edit', 'delete']
)
const canEdit = computed(() => !props.readonly)

const loading = ref(false)
const saving = ref(false)
const rows = ref<RemoteRow[]>([])
const sources = ref<Record<string, SourceItem[]>>({})

const isModalOpen = ref(false)
const editing = ref<RemoteRow | null>(null)
const removing = ref<RemoteRow | null>(null)
const form = ref<RemoteRow>({})
const filters = ref<Record<string, string>>({})

const translate = (text: string): string => {
  const translated = t(text)
  return translated === text ? text : translated
}

const interpolate = (text: string, params: Record<string, string>): string =>
  text.replace(/\{([A-Za-z0-9_]+)\}/g, (match, key: string) =>
    key in params ? params[key] : match
  )

const message = (
  key: keyof Omit<RemoteTableMessages, 'errors'>,
  params: Record<string, string> = {}
): string => {
  const custom = config.value.messages?.[key]
  return custom
    ? interpolate(translate(custom), params)
    : t(`remoteTable.${key}`, params)
}

const title = computed(() => translate(props.field.label))

const rowId = (row: RemoteRow): string => String(row[idKey.value] ?? '')

const sourceItems = (name?: string): SourceItem[] =>
  name ? sources.value[name] ?? [] : []

const sourceName = (name: string, id: unknown): string | undefined =>
  sourceItems(name).find(_ => _.id === String(id ?? ''))?.name

const displayValue = (column: RemoteColumn, row: RemoteRow): unknown =>
  column.source
    ? sourceName(column.source, row[column.name]) ?? row[column.name]
    : row[column.name]

const displayValues = (row: RemoteRow): Record<string, string> =>
  Object.fromEntries(
    config.value.columns.map(column => [
      column.name,
      String(displayValue(column, row) ?? '')
    ])
  )

const columns = computed(() => [
  ...config.value.columns.map(column => ({
    name: column.name,
    title: translate(column.title)
  })),
  { name: 'actions', title: '' }
])

const tableRows = computed(() =>
  rows.value.map(row => ({
    ...Object.fromEntries(
      config.value.columns.map(column => [
        column.name,
        displayValue(column, row)
      ])
    ),
    id: rowId(row)
  }))
)

const selectableItems = (field: RemoteField): SourceItem[] => {
  const source = field.source ? config.value.sources?.[field.source] : undefined
  return sourceItems(field.source).filter(_ =>
    matchesSourceFilter(_.raw, source?.filter)
  )
}

const selectedItemOf = (fieldName: string): SourceItem | undefined => {
  const field = config.value.fields.find(_ => _.name === fieldName)
  const current = String(form.value[fieldName] ?? '')
  return sourceItems(field?.source).find(_ => _.id === current)
}

const applyOptionFilter = (
  items: SourceItem[],
  filter?: RemoteOptionFilter
): SourceItem[] => {
  if (!filter) return items
  const expected = selectedItemOf(filter.field)?.raw[filter.sourceKey]
  if (expected === undefined || expected === null || expected === '') return []
  return items.filter(
    _ => String(_.raw[filter.optionKey] ?? '') === String(expected)
  )
}

const isDependencyPending = (field: RemoteField): boolean =>
  Boolean(field.dependsOn) && !form.value[field.dependsOn ?? '']

const isDisabled = (field: RemoteField): boolean =>
  (Boolean(editing.value) && field.editable === false) ||
  isDependencyPending(field)

const availableOptionsFor = (field: RemoteField): SourceItem[] => {
  const current = String(form.value[field.name] ?? '')
  let items = applyOptionFilter(selectableItems(field), field.filterBy)
  const unique = config.value.uniqueBy ?? []
  if (unique.includes(field.name)) {
    const others = unique.filter(_ => _ !== field.name)
    const used = new Set(
      rows.value
        .filter(row => !editing.value || rowId(row) !== rowId(editing.value))
        .filter(row =>
          others.every(
            name => String(row[name] ?? '') === String(form.value[name] ?? '')
          )
        )
        .map(row => String(row[field.name] ?? ''))
    )
    items = items.filter(_ => !used.has(_.id) || _.id === current)
  }

  return items
}

const optionsFor = (field: RemoteField) => {
  const current = String(form.value[field.name] ?? '')
  if (editing.value && field.editable === false) {
    return sourceItems(field.source)
      .filter(_ => _.id === current)
      .map(_ => ({ id: _.id, name: _.name }))
  }

  const items = availableOptionsFor(field)
  const search = (filters.value[field.name] ?? '').toLowerCase()
  return items
    .filter(_ => _.name.toLowerCase().includes(search))
    .map(_ => ({ id: _.id, name: _.name }))
}

const defaultFor = (field: RemoteField): unknown =>
  field.default ?? (field.type === 'check' ? false : '')

const emptyForm = (): RemoteRow =>
  Object.fromEntries(
    config.value.fields.map(field => [field.name, defaultFor(field)])
  )

const blockedByEmptySource = computed(() =>
  config.value.fields.some(
    field =>
      field.type === 'select' &&
      field.required &&
      selectableItems(field).length === 0
  )
)

const requiredFieldWithoutOptions = computed(() =>
  config.value.fields.find(
    field =>
      field.type === 'select' &&
      field.required &&
      !form.value[field.name] &&
      !isDependencyPending(field) &&
      availableOptionsFor(field).length === 0
  )
)

const canSave = computed(
  () =>
    !saving.value &&
    config.value.fields
      .filter(field => field.required)
      .every(field => {
        const value = form.value[field.name]
        return value !== undefined && value !== null && value !== ''
      })
)

const load = async () => {
  if (!canLoad.value) return
  loading.value = true
  try {
    const sourceEntries = Object.entries(config.value.sources ?? {})
    const [loadedRows, ...loadedSources] = await Promise.all([
      listRemoteRows(config.value, tagContext.value),
      ...sourceEntries.map(async ([name, source]) => ({
        name,
        items: await loadRemoteSource(source, tagContext.value)
      }))
    ])
    rows.value = loadedRows
    sources.value = Object.fromEntries(
      loadedSources.map(_ => [_.name, _.items])
    )
  } catch (error) {
    notifyError(error)
  } finally {
    loading.value = false
  }
}

const openCreate = () => {
  editing.value = null
  form.value = emptyForm()
  filters.value = {}
  isModalOpen.value = true
}

const openEdit = (id: string) => {
  const row = rows.value.find(_ => rowId(_) === id)
  if (!row) return
  editing.value = row
  form.value = Object.fromEntries(
    config.value.fields.map(field => [
      field.name,
      row[field.name] ?? defaultFor(field)
    ])
  )
  filters.value = {}
  isModalOpen.value = true
}

const closeModal = () => {
  isModalOpen.value = false
  editing.value = null
}

const onFieldChange = (field: RemoteField, value: unknown) => {
  form.value[field.name] =
    field.type === 'number' && value !== '' ? Number(value) : value
  for (const name of field.resets ?? []) {
    const reset = config.value.fields.find(_ => _.name === name)
    if (reset) form.value[name] = defaultFor(reset)
  }
}

const valuesFor = (fields: RemoteField[]): RemoteRow =>
  Object.fromEntries(fields.map(field => [field.name, form.value[field.name]]))

const save = async () => {
  if (!canSave.value) return
  saving.value = true
  try {
    if (editing.value) {
      await updateRemoteRow(
        config.value,
        rowId(editing.value),
        valuesFor(config.value.fields.filter(_ => _.editable !== false))
      )
      notifySuccess('updated')
    } else {
      await createRemoteRow(
        config.value,
        tagContext.value,
        valuesFor(config.value.fields)
      )
      notifySuccess('created')
    }
    closeModal()
    await load()
  } catch (error) {
    notifyError(error)
  } finally {
    saving.value = false
  }
}

const confirmRemove = (id: string) => {
  removing.value = rows.value.find(_ => rowId(_) === id) ?? null
}

const remove = async () => {
  if (!removing.value) return
  saving.value = true
  try {
    await deleteRemoteRow(config.value, rowId(removing.value))
    notifySuccess('removed')
    removing.value = null
    await load()
  } catch (error) {
    notifyError(error)
  } finally {
    saving.value = false
  }
}

const notifySuccess = (key: 'created' | 'updated' | 'removed') => {
  notifications.addNotification({
    title: title.value,
    text: message(key),
    type: 'success',
    time: 8000
  })
}

const notifyError = (error: any) => {
  const code: string = error?.response?.data?.code ?? 'default'
  const custom = config.value.messages?.errors?.[code]
  const generic = `remoteTable.errors.${code}`
  const text = custom
    ? translate(custom)
    : t(generic) !== generic
    ? t(generic)
    : error?.response?.data?.message ?? t('remoteTable.errors.default')
  notifications.addNotification({
    title: title.value,
    text,
    type: 'error',
    time: 10000
  })
}

onMounted(load)
</script>
<template>
  <div class="sm:col-span-6 lg:col-span-6 xl:col-span-6" :class="class">
    <DLoading v-show="loading || saving" :message="t('loading.data')" />

    <div
      v-if="!canLoad"
      class="bg-white rounded-md p-4 text-sm text-gray-500"
      v-html="message('saveParentFirst')" />

    <template v-else>
      <div class="bg-white pb-4 w-full">
        <div class="flex justify-end">
          <DBtn
            v-if="canEdit && actions.includes('add')"
            :icon="'PlusIcon'"
            type="button"
            :disabled="blockedByEmptySource"
            @click="openCreate()">
            {{ message('add') }}
          </DBtn>
        </div>
        <p
          v-if="!loading && blockedByEmptySource"
          class="text-sm text-amber-600 mt-2"
          v-html="message('emptySources')" />
      </div>

      <DTable
        :columns="columns"
        :rows="tableRows"
        :total-rows="tableRows.length"
        :select-options="false"
        :pagination-limit="false"
        :text-no-data="t('table.noData')"
        :text-per-page="t('table.rowsPerPage')"
        :table-scroll="false">
        <template v-if="canEdit" #actions="data: any">
          <div class="flex py-2.5 justify-end gap-1">
            <Popper
              v-if="actions.includes('edit')"
              arrow
              offsetDistance="12"
              :content="t('table.action.edit')"
              :hover="true"
              placement="top"
              class="tooltip">
              <div
                class="bg-gray-50 rounded-md py-2"
                @click="openEdit(data.id)">
                <Edit03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
              </div>
            </Popper>
            <Popper
              v-if="actions.includes('delete')"
              arrow
              offsetDistance="12"
              :content="t('table.action.remove')"
              :hover="true"
              placement="top"
              class="tooltip">
              <div
                class="bg-gray-50 rounded-md py-2"
                @click="confirmRemove(data.id)">
                <Trash03Icon
                  class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
              </div>
            </Popper>
          </div>
        </template>
      </DTable>
    </template>

    <DModal
      :open="isModalOpen"
      :title="editing ? message('edit') : message('add')"
      size="md:w-full lg:w-2/3 xl:w-1/2"
      :form="true"
      @close-modal="closeModal"
      @keydown.esc="closeModal">
      <template #form>
        <div class="text-lg text-gray-800 font-semibold mb-4">
          {{ editing ? message('edit') : message('add') }}
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
          <template
            v-for="remoteField in config.fields"
            :key="remoteField.name">
            <DSelectFieldSearch
              v-if="remoteField.type === 'select'"
              :model-value="String(form[remoteField.name] ?? '')"
              :label="translate(remoteField.label)"
              :placeholder="
                remoteField.placeholder
                  ? translate(remoteField.placeholder)
                  : t('form.button.select')
              "
              :options="optionsFor(remoteField)"
              :disabled="isDisabled(remoteField)"
              :required="Boolean(remoteField.required)"
              @update:model-value="onFieldChange(remoteField, $event)"
              @filter="filters[remoteField.name] = $event" />
            <DToggleField
              v-else-if="remoteField.type === 'check'"
              :model-value="Boolean(form[remoteField.name])"
              :label="translate(remoteField.label)"
              :disabled="isDisabled(remoteField)"
              hint=""
              help-text=""
              @update:model-value="onFieldChange(remoteField, $event)" />
            <DTextField
              v-else
              :model-value="(form[remoteField.name] as string | number) ?? ''"
              :type="remoteField.type"
              :label="translate(remoteField.label)"
              :placeholder="
                remoteField.placeholder
                  ? translate(remoteField.placeholder)
                  : ''
              "
              :disabled="isDisabled(remoteField)"
              :required="Boolean(remoteField.required)"
              @update:model-value="onFieldChange(remoteField, $event)" />
          </template>
        </div>
        <p
          v-if="requiredFieldWithoutOptions"
          class="text-sm text-amber-600 mt-4"
          v-html="message('emptySources')" />
        <div class="flex justify-end mt-6">
          <DBtn
            type="button"
            color="secondary"
            class="mr-3"
            @click="closeModal">
            {{ t('form.button.cancel') }}
          </DBtn>
          <DBtn type="button" :disabled="!canSave" @click="save">
            {{ editing ? t('form.button.update') : t('form.button.save') }}
          </DBtn>
        </div>
      </template>
      <template #actions>
        <div></div>
      </template>
    </DModal>

    <DModal
      :open="Boolean(removing)"
      :title="message('remove')"
      type-alert="question"
      :description="
        message('removeQuestion', removing ? displayValues(removing) : {})
      "
      :accept-question="t('form.button.confirm')"
      :name-button-close="t('form.button.cancel')"
      @close-modal="removing = null"
      @other-method="remove" />
  </div>
</template>
